import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CRow,
  CSmartTable,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
  CModalFooter,
  CTableRow,
  CTableDataCell,
  CTableBody,
  CTableHeaderCell,
  CModalHeader,
  CModal,
  CModalBody,
  CTable,
  CTableHead,
} from '@coreui/react-pro'
import { toast } from 'react-toastify'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent.js'
import UserService from 'src/service/UserService'
import CommonService from 'src/service/CommonService'

const AdminPricingPlan = () => {
  const [loading, setLoading] = useState()
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState([])

  const gameTypeLabels = {
    1: 'Season Game',
    2: 'Tournament Game (For Leagues)',
    3: 'Tournament Game (For Indoor Leagues)',
    4: 'Match Contest',
  }

  // ---------------------------
  // PRICING TABLES
  // ---------------------------

  // League / Tournament pricing (game_type = 2)
  const leaguePricing = [
    { tier: '1–8 Teams', min_teams: 1, max_teams: 8, fee: 400 },
    { tier: '9–16 Teams', min_teams: 9, max_teams: 16, fee: 800 },
    { tier: '17+ Teams', min_teams: 17, max_teams: null, fee: 1200 },
  ]

  // Club-based pricing (game_type = 1)
  const clubPricing = [
    { tier: '1–4 Teams', fee: 300 },
    { tier: '5–8 Teams', fee: 420 },
    { tier: '9–12 Teams', fee: 540 },
    { tier: '13–16 Teams', fee: 660 },
    { tier: '17–20 Teams', fee: 780 },
    { tier: '21+ Teams', fee: 900 },
  ]

  // ---------------------------

  useEffect(() => {
    fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  const fetchData = (activePage, columnFilter, columnSorter, itemsPerPage) => {
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })

    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    UserService.adminPricingPlanUsers(offset, itemsPerPage, activePage, params).then((result) => {
      setUsers(result.data)
      setLoading(false)
    })
  }

  const [visibleModal, setVisibleModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedGameType, setSelectedGameType] = useState(null)
  const [paidPlanTypes, setPaidPlanTypes] = useState([])

  // ---------------------------
  // OPEN PRICING MODAL BASED ON GAME TYPE
  // ---------------------------
  const showPriceTable = (userId, extra, gameType) => {
    setSelectedUserId(userId)
    setSelectedGameType(gameType)

    CommonService.getPurchasePricingPlanByUser(userId).then((res) => {
      setPaidPlanTypes(res?.data || [])
    })

    setVisibleModal(true)
  }

  // ---------------------------
  // HANDLE PAYMENT
  // ---------------------------
  const handlePayNow = (fee, tier, planType, min, max) => {
    const quoteData = {
      user_id: selectedUserId,
      plan_type: planType,
      team_tier: tier,
      min_teams: min,
      max_teams: max,
      amount: fee,
    }

    CommonService.saveGameUserPricingPlanByAdmin(quoteData)
      .then((res) => {
        ToastComponent(res?.message || 'Pricing plan saved successfully', 'success')
        fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
        setVisibleModal(false)
      })
      .catch((error) => {
        const status = error?.status
        const message = error?.message

        if (status === 409 && message) {
          ToastComponent(message, 'error')
        } else {
          ToastComponent('Error saving pricing quota', 'error')
        }
      })
  }

  const columns = [
    { label: 'Hub Name', key: 'club_name' },
    { label: 'Game Name', key: 'game_name' },
    { label: 'Game Type', key: 'game_type' },
    { label: 'Email', key: 'email' },
    { label: 'Status', key: 'membership_is_paid', filter: false },
    { key: 'action', label: 'Action', filter: false, sorter: false },
  ]

  return (
    <>
      <CRow>
        <CAccordion activeItemKey={1}>
          <CAccordionItem itemKey={1}>
            <CAccordionHeader>
              <strong>Do It Yourself</strong>
            </CAccordionHeader>
            <CAccordionBody>
              <CSmartTable
                columns={columns}
                columnFilter={{ external: true }}
                columnSorter={{ external: true }}
                scopedColumns={{
                  membership_is_paid: (item) => (
                    <td>
                      {item?.membership_is_paid ? (
                        <CBadge color="success">Paid</CBadge>
                      ) : (
                        <CBadge color="danger">Unpaid</CBadge>
                      )}
                    </td>
                  ),
                  game_type: (item) => <td>{gameTypeLabels[item.game_type] || '-'}</td>,
                  action: (item) => (
                    <td>
                      <CButton
                        color="primary"
                        variant="outline"
                        size="sm"
                        onClick={() => showPriceTable(item.game_id, 0, item.game_type)}
                      >
                        Show Price
                      </CButton>
                    </td>
                  ),
                }}
                items={users?.data}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect
                loading={loading}
                pagination={{ external: true }}
                paginationProps={{
                  activePage: activePage,
                  pages: Math.ceil(users?.total / itemsPerPage) || 1,
                }}
                tableProps={{ hover: true, responsive: true }}
                onActivePageChange={(activePage) => setActivePage(activePage)}
                onColumnFilterChange={(filter) => {
                  setActivePage(1)
                  setColumnFilter(filter)
                }}
                onItemsPerPageChange={(itemsPerPage) => {
                  setActivePage(1)
                  setItemsPerPage(itemsPerPage)
                }}
                onSorterChange={(sorter) => setColumnSorter(sorter)}
              />
            </CAccordionBody>
          </CAccordionItem>
        </CAccordion>
      </CRow>

      {/* --------------------------- */}
      {/* PRICING MODAL */}
      {/* --------------------------- */}
      <CModal
        visible={visibleModal}
        onClose={() => setVisibleModal(false)}
        size="lg"
        backdrop="static"
      >
        <CModalHeader>
          <strong>Pricing Table</strong>
        </CModalHeader>

        <CModalBody>
          <CTable bordered className="mt-3">
            <CTableHead>
              <CTableRow color="warning">
                <CTableHeaderCell>Team Tiers</CTableHeaderCell>
                <CTableHeaderCell>Game Fee (in AUD)</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>

            <CTableBody>
              {(selectedGameType === 1 ? clubPricing : leaguePricing).map((item, index) => {
                const fee = parseInt(item.fee)
                const planType = index + 1
                const isPaid = paidPlanTypes.includes(planType)

                return (
                  <CTableRow key={index}>
                    <CTableDataCell>{item.tier}</CTableDataCell>
                    <CTableDataCell>AUD {fee}</CTableDataCell>

                    <CTableDataCell>
                      <CButton
                        color="primary"
                        size="sm"
                        disabled={isPaid}
                        onClick={() =>
                          handlePayNow(
                            fee,
                            item.tier,
                            planType,
                            item.min_teams || null,
                            item.max_teams || null,
                          )
                        }
                      >
                        {isPaid ? 'Paid' : 'Pay Now'}
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </CModalBody>

        <CModalFooter>
          <CButton color="secondary" onClick={() => setVisibleModal(false)}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AdminPricingPlan
